import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  // Base — Figma LUARY-DS2 buttons are always pill-shaped. Solid
  // variants get a bg-*/90 hover (crisper text feedback than opacity);
  // neutral variants get a bg-accent hover per Figma's actual bindings.
  "group/button inline-flex shrink-0 items-center justify-center rounded-full border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 aria-expanded:bg-primary/90",
        brand:
          "bg-brand text-brand-foreground hover:bg-brand/90 aria-expanded:bg-brand/90",
        // Figma's binding here is `hover:bg-accent`, but --secondary and
        // --accent currently resolve to the same token in light and dark,
        // so that hover would be silent. Self-referential bg-secondary/80
        // preserves visible feedback until the colour system diverges
        // those two tokens.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary/80",
        ghost: "text-accent-foreground hover:bg-accent aria-expanded:bg-accent",
        outline:
          "border-border bg-transparent text-accent-foreground hover:bg-accent aria-expanded:bg-accent",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:text-primary/80",
      },
      // Heights / paddings / text from Figma's per-size symbols:
      //   xs 24/8/12, sm 32/12/14, default 36/16/14, lg 40/24/14.
      // Icon-only sizes mirror those heights as square.
      size: {
        xs: "h-6 gap-1 px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        default:
          "h-9 gap-1.5 px-4 text-sm has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        lg: "h-10 gap-2 px-6 text-sm has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 [&_svg:not([class*='size-'])]:size-3.5",
        icon: "size-9",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * Renders a leading spinner, applies `opacity-70` (matching Figma's
     * loading state), and disables interaction. Combine with explicit
     * `disabled` for a permanently-disabled button; `loading` is for
     * transient async work.
     */
    loading?: boolean;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-loading={loading || undefined}
      disabled={loading || disabled}
      className={cn(
        buttonVariants({ variant, size }),
        // Override at the `disabled:` variant level so it wins over the
        // base's `disabled:opacity-50` (same pseudo-class specificity,
        // resolved correctly by tailwind-merge). A plain `opacity-70`
        // would lose to `disabled:opacity-50` since loading also sets
        // disabled, rendering the button at 50% instead of Figma's 70%.
        loading && "disabled:opacity-70",
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
